import { RedoMerchantClient } from "@redotech/redo-merchant-app-common/client";
import { getUsers } from "@redotech/redo-merchant-app-common/client/user";
import { ConversationFiltersV3 } from "@redotech/redo-model/conversation-filters/conversation-filters";
import { SortableConversationTableColumn } from "@redotech/redo-model/tables/conversations-table-sort";
import { TableSort } from "@redotech/redo-model/tables/table";
import { GetUser } from "@redotech/redo-model/user";
import { TableFetcher, YieldType } from "@redotech/redo-web/table";
import { overwriteCombineMaps } from "@redotech/util/map";
import { BehaviorSubject } from "rxjs";

export abstract class GenericConversationFetcher<
  T extends { _id: string; messages: { user?: string | null }[] },
> implements TableFetcher<T>
{
  constructor(
    protected readonly client: RedoMerchantClient,
    protected readonly userCache: Map<string, GetUser>,
    protected readonly onConversationsLoaded?: (() => void) | undefined,
  ) {}

  protected readonly conversationIdToConversationSubject = new BehaviorSubject<
    Map<string, T>
  >(new Map());

  public conversationIdToConversation =
    this.conversationIdToConversationSubject.asObservable();

  protected updateConversationsMap(conversations: T[]) {
    const originalMap = this.conversationIdToConversationSubject.value;
    const newMap = new Map(conversations.map((c) => [c._id, c]));
    this.conversationIdToConversationSubject.next(
      overwriteCombineMaps(originalMap, newMap),
    );
  }

  protected async fetchUsers(data: T[], signal?: AbortSignal): Promise<void> {
    const userIdsToFetch = new Set<string>();
    for (const conversation of data) {
      if (conversation.messages) {
        for (const message of conversation.messages) {
          if (
            message.user &&
            !this.userCache.has(message.user) &&
            !userIdsToFetch.has(message.user)
          ) {
            userIdsToFetch.add(message.user);
          }
        }
      }
    }
    if (userIdsToFetch.size > 0) {
      const users = await getUsers(this.client, {
        userIds: Array.from(userIdsToFetch),
        signal,
      });
      for (const user of users) {
        this.userCache.set(user._id, user);
      }
    }
  }

  protected abstract fetchConversations(params: {
    pageContinue?: string;
    pageStop?: string;
    pageSize: number;
    filters?: ConversationFiltersV3;
    sort?: TableSort<SortableConversationTableColumn>;
    signal?: AbortSignal;
  }): Promise<{ data: T[]; pageNext: string | undefined }>;

  async counts() {
    return {};
  }

  async *data(
    primaryFilter: string,
    filters: ConversationFiltersV3,
    search: string | undefined,
    sort: TableSort<SortableConversationTableColumn> | undefined,
    pageSize: number = 50,
    pageNumber?: number,
    signal?: AbortSignal,
    passThroughValues?: ConversationFiltersV3,
  ): AsyncIterator<YieldType<T>> {
    let cursor: string | undefined;
    let highestLoadedPage = 0;

    const refresh = async (
      pageStop: string | undefined,
      toPage: number | undefined,
      signal: AbortSignal | undefined,
    ) => {
      let items: T[] = [];
      let page = 1;
      for (let refreshCursor: string | undefined; ; ) {
        try {
          const { data, pageNext } = await this.fetchConversations({
            pageContinue: refreshCursor,
            pageStop,
            pageSize,
            filters: passThroughValues,
            sort: passThroughValues?.sort || sort,
            signal,
          });
          this.updateConversationsMap(data);
          await this.fetchUsers(data, signal);
          if (data && data.length > 0) {
            items = [...items, ...data];
          }
          if (page === toPage) {
            break;
          }
          ++page;
          if (pageNext) {
            refreshCursor = pageNext;
          } else {
            break;
          }
        } catch (e: any) {
          if (e.code === "ERR_CANCELED") {
            return [];
          }
          throw e;
        }
      }
      return items;
    };

    for (let pageContinue: string | undefined; ; ) {
      try {
        const { data, pageNext } = await this.fetchConversations({
          pageContinue,
          pageSize,
          filters: passThroughValues,
          sort: passThroughValues?.sort || sort,
          signal,
        });
        this.updateConversationsMap(data);
        await this.fetchUsers(data, signal);

        cursor = pageNext;
        highestLoadedPage++;
        this.onConversationsLoaded?.();
        yield {
          items: data,
          refresh: async (signal) => refresh(cursor, highestLoadedPage, signal),
        };
        if (pageNext === undefined) {
          break;
        }
        pageContinue = pageNext;
      } catch (e: any) {
        if (e.code !== "ERR_CANCELED") {
          throw e;
        }
        return {
          items: [],
          refresh: async (signal) => refresh(cursor, highestLoadedPage, signal),
          aborted: true,
        } as YieldType<T>;
      }
    }
  }
}
